// Shared Grievance Details Modal (UAS-2243)
// Used by both Internal Admin (UAS-2241) and Institute Admin screens.
// Self-fetching: loads GET /grievance/:id/detail when grievanceId changes.
// Status update: POST /grievance/:id/status on Save Changes.

import { useContext, useEffect, useState } from 'react';
import { axiosClient } from '../../../../library/Service/axiosClient';
import TagChip from './TagChip';
import {
    getPriorityStyle,
    getPriorityLabel,
    getStatusStyle,
    V2_STATUS_OPTIONS,
} from './helpers/statusBadge';
import type { GrievanceTag } from '../services/InstituteAdminGrievanceService';
import { getStudentImage } from '../../../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../../../library/SimpleControls/InitialsAvatar';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass, getDomainValueForCode } from '../../../../library/Core/SmartFunctions';

const BASE = process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER;

// ── Types ────────────────────────────────────────────────────────────────────

interface GrievanceAlumni {
    uuid: string | null;
    name: string;
    tier: string | null;
    program: string | null;
    batch: string | null;
    avatarUrl: string | null;
    genderCode: string | null;
}

interface GrievancePushDetail {
    id: number;
    institutePriority: string | null;
    teamNotes: string;
    suggestedResolution: string;
    tags: GrievanceTag[];
}

interface TimelineEntry {
    id: number;
    eventType: string;
    status: string | null;
    remarks: string | null;
    authorName: string;
    createdAt: string;
}

export interface GrievanceDetail {
    id: number;
    code: string;
    status: string;
    priority: string;
    category: string;
    isInternalOnly: boolean;
    mode: string | null;
    description: string | null;
    receivedDate: string;
    updatedAt: string;
    alumni: GrievanceAlumni;
    push: GrievancePushDetail | null;
    timeline: TimelineEntry[];
}

export interface GrievanceDetailsModalProps {
    grievanceId: number | null;
    onClose: () => void;
    onStatusUpdated?: (id: number, newStatus: string) => void;
    /** Internal admin: show both original priority and institute-override priority side by side */
    showBothPriorities?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Event type → human-readable timeline label
const EVENT_LABELS: Record<string, string> = {
    created:       'Grievance Created',
    pushed:        'Pushed to Institute',
    statusUpdated: 'Status Updated',
    responseAdded: 'Response Added',
    undone:        'Push Undone',
};

// Event type → Bootstrap icon class
const EVENT_ICONS: Record<string, string> = {
    created:       'bi-file-earmark-text',
    pushed:        'bi-send-fill',
    statusUpdated: 'bi-arrow-clockwise',
    responseAdded: 'bi-chat-left-text-fill',
    undone:        'bi-x-circle',
};

/** Build two-letter avatar initials from a full name. */
const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

/**
 * Status display label — uses V2_STATUS_OPTIONS so internal admin sees "New"
 * (not "Pending Action", which is institute-admin-specific terminology).
 */
const getStatusLabel = (status: string): string => {
    const opt = V2_STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? opt.label : status;
};

// ── Component ─────────────────────────────────────────────────────────────────

const GrievanceDetailsModal = ({ grievanceId, onClose, onStatusUpdated, showBothPriorities = false }: GrievanceDetailsModalProps) => {
    const { state } = useContext(SmartContext);
    const [detail, setDetail]           = useState<GrievanceDetail | null>(null);
    const [loading, setLoading]         = useState(false);
    const [fetchError, setFetchError]   = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [saving, setSaving]           = useState(false);
    const [saveError, setSaveError]     = useState<string | null>(null);

    // Fetch full detail whenever the modal opens (grievanceId changes from null → id)
    useEffect(() => {
        if (!grievanceId) {
            setDetail(null);
            return;
        }

        setLoading(true);
        setFetchError(null);
        setSelectedStatus('');
        setSaveError(null);

        axiosClient()
            .get(`${BASE}/grievance/${grievanceId}/detail`)
            .then((res) => {
                const d = res.data?.data ?? null;
                setDetail(d);
                if (d?.status) setSelectedStatus(d.status);
            })
            .catch(() => setFetchError('Failed to load grievance details. Please try again.'))
            .finally(() => setLoading(false));
    }, [grievanceId]);

    // AC10: Save Changes — commit status if changed, then close
    const handleSave = async () => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        if (!selectedStatus || !detail) {
            onClose();
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            await axiosClient().post(`${BASE}/grievance/${detail.id}/status`, { status: selectedStatus });
            onStatusUpdated?.(detail.id, selectedStatus);
            onClose();
        } catch {
            setSaveError('Failed to save status. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (!grievanceId) return null;

    const effectivePriority = detail ? (detail.push?.institutePriority ?? detail.priority) : null;
    const priorityStyle = effectivePriority ? getPriorityStyle(effectivePriority) : null;
    const statusStyle   = detail ? getStatusStyle(detail.status) : null;

    return (
        <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable mx-auto"
                style={{ maxWidth: 600 }}
            >
                <div className="modal-content radius-15 d-flex flex-column" style={{ maxHeight: '90vh' }}>

                    {/* ── AC1, AC11: Sticky modal header ───────────────────── */}
                    <div className="modal-header flex-column justify-content-start align-items-start flex-shrink-0">
                        <h5 className="modal-title fw-medium">Grievance Details</h5>
                        {/* AC12: Grievance ID in monospace / code-style font */}
                        {detail && (
                            <p className="text-secondary mb-0" style={{ fontFamily: 'monospace' }}>
                                {detail.code}
                            </p>
                        )}
                        <a
                            href="#"
                            className="position-absolute top-20 right-20 m-0 text-secondary font-30"
                            onClick={(e) => { e.preventDefault(); onClose(); }}
                            aria-label="Close"
                        >
                            <i className="bi bi-x-circle-fill"></i>
                        </a>
                    </div>

                    {/* ── Scrollable body ───────────────────────────────────── */}
                    <div className="modal-body p-4 overflow-auto flex-grow-1">

                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading…</span>
                                </div>
                            </div>
                        )}

                        {fetchError && !loading && (
                            <div className="alert alert-danger">{fetchError}</div>
                        )}

                        {!loading && !fetchError && detail && (
                            <>
                                {/* ── AC2: Status bar ────────────────────────────────── */}
                                <div className="d-flex flex-wrap align-items-center gap-4 pb-3 mb-3 border rounded-3 p-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-secondary small">Current Status</span>
                                        <span
                                            className="badge rounded-pill font-500"
                                            style={{ backgroundColor: statusStyle!.bg, color: statusStyle!.text }}
                                        >
                                            {getStatusLabel(detail.status)}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="text-secondary small">Priority</span>
                                        {showBothPriorities && detail.push?.institutePriority && detail.push.institutePriority !== detail.priority ? (
                                            <>
                                                <span
                                                    className="badge rounded-pill font-500 text-decoration-line-through opacity-75"
                                                    style={{ backgroundColor: getPriorityStyle(detail.priority).bg, color: getPriorityStyle(detail.priority).text }}
                                                >
                                                    {getPriorityLabel(detail.priority)}
                                                </span>
                                                <span className="text-secondary small">→</span>
                                                <span
                                                    className="badge rounded-pill font-500"
                                                    style={{ backgroundColor: priorityStyle!.bg, color: priorityStyle!.text }}
                                                >
                                                    {getPriorityLabel(effectivePriority!)}
                                                    <span className="ms-1 opacity-75" style={{ fontSize: '0.7em' }}>(Institute)</span>
                                                </span>
                                            </>
                                        ) : (
                                            <span
                                                className="badge rounded-pill font-500"
                                                style={{ backgroundColor: priorityStyle!.bg, color: priorityStyle!.text }}
                                            >
                                                {getPriorityLabel(effectivePriority!)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* ── AC14: Alumni Information (existing) ────────────── */}
                                <div className="pb-3 mb-3 border-bottom">
                                    <h6 className="text-uppercase text-secondary small fw-semibold mb-3">Alumni Information</h6>
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        {hasProfilePicture(detail.alumni.avatarUrl) ? (
                                            <img
                                                src={getStudentImage({ profilePictureFilePath: detail.alumni.avatarUrl, genderCode: detail.alumni.genderCode })}
                                                alt={detail.alumni.name}
                                                className="rounded-circle flex-shrink-0"
                                                style={{ width: 48, height: 48, objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <InitialsAvatar name={detail.alumni.name} size={48} />
                                        )}
                                        <div>
                                            <div className="fw-semibold">{detail.alumni.name || '—'}</div>
                                            <div className="text-secondary small">
                                                {detail.alumni.tier
                                                    ? `${detail.alumni.tier} · `
                                                    : ''}
                                                {detail.alumni.uuid ? 'Profile Linked' : 'Manual Entry'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row g-3 mb-3">
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Program</div>
                                            <div>{detail.alumni.program || '—'}</div>
                                        </div>
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Batch Year</div>
                                            <div>{detail.alumni.batch || '—'}</div>
                                        </div>
                                    </div>

                                    {/* ── AC3, AC4: Alumni Importance (always present) ── */}
                                    <h6 className="text-uppercase text-secondary small fw-semibold mb-2">Alumni Importance</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {(detail.push?.tags ?? []).length > 0 ? (
                                            detail.push!.tags.map((tag) => (
                                                <TagChip key={tag.tagCode} tag={tag} />
                                            ))
                                        ) : (
                                            <span className="text-secondary small fst-italic">No tags assigned</span>
                                        )}
                                    </div>
                                </div>

                                {/* ── AC14: Grievance Details (existing) ─────────────── */}
                                <div className="pb-3 mb-3 border-bottom">
                                    <h6 className="text-uppercase text-secondary small fw-semibold mb-3">Grievance Details</h6>
                                    <div className="row g-3 mb-3">
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Category</div>
                                            <div>{getDomainValueForCode(detail.category, 'GRIEVANCE_CATEGORY', state) || detail.category}</div>
                                        </div>
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Mode of Communication</div>
                                            <div>{detail.mode ? (getDomainValueForCode(detail.mode, 'GRIEVANCE_COMMUNICATION_MODE', state) || detail.mode) : '—'}</div>
                                        </div>
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Created on</div>
                                            <div className="fw-semibold">{detail.receivedDate}</div>
                                        </div>
                                        <div className="col-6">
                                            <div className="text-secondary small mb-1">Last Updated</div>
                                            <div className="fw-semibold">{detail.updatedAt}</div>
                                        </div>
                                    </div>
                                    {detail.description && (
                                        <>
                                            <div className="text-secondary small mb-1">Description</div>
                                            <div
                                                className="bg-light border rounded-3 p-3"
                                                dangerouslySetInnerHTML={{ __html: detail.description }}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* ── AC5, AC7: Univariety Team Notes (conditional) ──── */}
                                {/* AC5: Section absent entirely when push has no teamNotes */}
                                {detail.push?.teamNotes && (
                                    <div className="pb-3 mb-3 border-bottom">
                                        <h6 className="text-uppercase text-secondary small fw-semibold mb-2">
                                            <i className="bi bi-building me-1"></i>Univariety Team Notes
                                        </h6>
                                        {/* AC7: informational-tone (primary-subtle) background */}
                                        <div
                                            className="bg-primary-subtle text-primary-emphasis border border-primary-subtle rounded-3 p-3"
                                            dangerouslySetInnerHTML={{ __html: detail.push.teamNotes }}
                                        />
                                    </div>
                                )}

                                {/* ── AC6, AC7: Suggested Resolution (conditional) ────── */}
                                {/* AC6: Section absent entirely when push has no suggestedResolution */}
                                {detail.push?.suggestedResolution && (
                                    <div className="pb-3 mb-3 border-bottom">
                                        <h6 className="text-uppercase text-success small fw-semibold mb-2">
                                            <i className="bi bi-lightbulb-fill me-1"></i>Suggested Resolution
                                        </h6>
                                        {/* AC7: success-tone background */}
                                        <div
                                            className="bg-success-subtle text-success-emphasis border border-success-subtle rounded-3 p-3"
                                            dangerouslySetInnerHTML={{ __html: detail.push.suggestedResolution }}
                                        />
                                    </div>
                                )}

                                {/* ── Update Status ──────────────────────────────────── */}
                                <div className="pb-3 mb-3 border-bottom">
                                    <h6 className="text-uppercase text-secondary small fw-semibold mb-3">Update Status</h6>
                                    <label htmlFor="grievanceDetailStatus" className="form-label">
                                        Change Status
                                    </label>
                                    {/* AC15: Canonical dropdown order; locked once resolved or discarded */}
                                    <select
                                        id="grievanceDetailStatus"
                                        className="form-select"
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        disabled={detail.status === 'resolved' || detail.status === 'discarded'}
                                    >
                                        <option value="">— Select —</option>
                                        {V2_STATUS_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {detail.status === 'resolved' && (
                                        <div className="form-text text-success mt-1">
                                            <i className="bi bi-check-circle-fill me-1"></i>
                                            This grievance is resolved and its status cannot be changed.
                                        </div>
                                    )}
                                    {detail.status === 'discarded' && (
                                        <div className="form-text text-secondary mt-1">
                                            <i className="bi bi-x-circle-fill me-1"></i>
                                            This grievance is discarded and its status cannot be changed.
                                        </div>
                                    )}
                                </div>

                                {/* ── AC9, AC13: Activity Timeline ───────────────────── */}
                                <div>
                                    <h6 className="text-uppercase text-secondary small fw-semibold mb-3">
                                        Activity Timeline
                                    </h6>
                                    <ul className="list-unstyled timeline-dot mb-0">
                                        {detail.timeline.length === 0 && (
                                            <li className="text-secondary small fst-italic">
                                                No activity recorded yet.
                                            </li>
                                        )}
                                        {detail.timeline.map((entry, idx) => {
                                            // First entry with status 'new' is the grievance creation event
                                            const isCreationEntry = idx === 0 && entry.status === 'new';
                                            const effectiveEventType = isCreationEntry ? 'created' : entry.eventType;
                                            const icon  = EVENT_ICONS[effectiveEventType] ?? 'bi-circle';
                                            const label = EVENT_LABELS[effectiveEventType] ?? effectiveEventType;
                                            const isLast = idx === detail.timeline.length - 1;
                                            return (
                                                <li
                                                    key={entry.id}
                                                    className={`d-flex gap-3${isLast ? '' : ' mb-3'}`}
                                                >
                                                    <div className="rounded-circle timeline-dot-circle d-flex justify-content-center align-items-center flex-shrink-0">
                                                        <i className={`bi ${icon}`}></i>
                                                    </div>
                                                    <div>
                                                        <div className="fw-medium d-flex align-items-center gap-2 flex-wrap">
                                                            {label}
                                                            {/* Show status badge for status-change events (not for creation entry) */}
                                                            {entry.status && !isCreationEntry && (
                                                                <span
                                                                    className="badge rounded-pill font-12 font-500 px-2 py-1"
                                                                    style={{
                                                                        backgroundColor: getStatusStyle(entry.status).bg,
                                                                        color: getStatusStyle(entry.status).text,
                                                                    }}
                                                                >
                                                                    {getStatusLabel(entry.status)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* AC9: date + time + author name */}
                                                        <div className="text-secondary small">
                                                            {entry.createdAt} · {entry.authorName}
                                                        </div>
                                                        {/* AC13: Comment text in a distinct note block */}
                                                        {entry.remarks && (
                                                            <div className="bg-light border rounded-3 p-2 small mt-2">
                                                                {entry.remarks}
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                {/* Inline save error — modal stays open on network failure */}
                                {saveError && (
                                    <div className="alert alert-danger mt-3 mb-0">{saveError}</div>
                                )}
                            </>
                        )}
                    </div>

                    {/* ── AC10, AC11: Sticky footer ─────────────────────────── */}
                    <div className="modal-footer flex-shrink-0">
                        <button
                            type="button"
                            className="btn btn-border-gray px-3"
                            onClick={onClose}
                            disabled={saving}
                        >
                            <span>Cancel</span>
                        </button>
                        <button
                            type="button"
                            className={`btn btn-green px-3 height-35 font-400 ${getActionElementClass()}`}
                            onClick={handleSave}
                            disabled={saving || loading || detail?.status === 'resolved' || detail?.status === 'discarded'}
                        >
                            <span className="font-400">
                                {saving ? 'Saving…' : 'Save Changes'}
                            </span>
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default GrievanceDetailsModal;
